/**
 * Message data models with Zod schemas
 * Explicit schema definitions with parsing validation
 */
import { parseISO } from "date-fns";
import { z } from "zod";

// Enums
export const MessageDirectionSchema = z.enum(["inbound", "outbound"]);
export const MessageStatusSchema = z.enum([
  "queued",
  "sent",
  "delivered",
  "failed",
  "received",
]);

export type MessageDirection = z.infer<typeof MessageDirectionSchema>;
export type MessageStatus = z.infer<typeof MessageStatusSchema>;

// Domain schema (camelCase)
const MessageSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  businessId: z.string(),
  conversationId: z.string().nullable(),
  phoneNumberId: z.string(),
  fromNumber: z.string(),
  toNumber: z.string(),
  direction: MessageDirectionSchema,
  status: MessageStatusSchema,
  body: z.string(),
  twilioMessageSid: z.string().nullable(),
  sentAt: z.date().nullable(),
  receivedAt: z.date().nullable(),
  deletedAt: z.date().nullable(),
  createdBy: z.string().nullable(),
});

export const CreateMessageRequestSchema = MessageSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  deletedAt: true,
  twilioMessageSid: true,
  sentAt: true,
  receivedAt: true,
}).partial({
  conversationId: true,
  status: true,
  createdBy: true,
});

export const MessageSendIntentSchema = z.object({
  idempotencyKey: z.uuid(),
  request: CreateMessageRequestSchema.readonly(),
}).readonly();

const UpdateMessageRequestSchema = MessageSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  businessId: true,
  conversationId: true,
  phoneNumberId: true,
  fromNumber: true,
  toNumber: true,
  direction: true,
  body: true,
  deletedAt: true,
  createdBy: true,
}).partial();

// DTO schema (snake_case)
const MessageDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  business_id: z.string(),
  conversation_id: z.string().nullable(),
  phone_number_id: z.string(),
  from_number: z.string(),
  to_number: z.string(),
  direction: MessageDirectionSchema,
  status: MessageStatusSchema,
  body: z.string(),
  twilio_message_sid: z.string().nullable(),
  sent_at: z.string().nullable(),
  received_at: z.string().nullable(),
  deleted_at: z.string().nullable(),
  created_by: z.string().nullable(),
});

export const CreateMessageDtoSchema = MessageDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  deleted_at: true,
  twilio_message_sid: true,
  sent_at: true,
  received_at: true,
}).partial({
  conversation_id: true,
  status: true,
  created_by: true,
});

export const UpdateMessageDtoSchema = MessageDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  business_id: true,
  conversation_id: true,
  phone_number_id: true,
  from_number: true,
  to_number: true,
  direction: true,
  body: true,
  deleted_at: true,
  created_by: true,
}).partial();

// Infer DTO types
export type MessageDto = z.infer<typeof MessageDtoSchema>;
export type CreateMessageDto = z.infer<typeof CreateMessageDtoSchema>;
export type UpdateMessageDto = z.infer<typeof UpdateMessageDtoSchema>;

// Infer domain types
export type Message = z.infer<typeof MessageSchema>;
export type CreateMessageRequest = z.infer<typeof CreateMessageRequestSchema>;
export type MessageSendIntent = z.infer<typeof MessageSendIntentSchema>;
export type UpdateMessageRequest = z.infer<typeof UpdateMessageRequestSchema>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromMessageDtoToMessage(dto: MessageDto): Message {
  const messageDto = MessageDtoSchema.parse(dto);

  return {
    id: messageDto.id,
    createdAt: parseISO(messageDto.created_at),
    modifiedAt: parseISO(messageDto.modified_at),
    businessId: messageDto.business_id,
    conversationId: messageDto.conversation_id,
    phoneNumberId: messageDto.phone_number_id,
    fromNumber: messageDto.from_number,
    toNumber: messageDto.to_number,
    direction: messageDto.direction,
    status: messageDto.status,
    body: messageDto.body,
    twilioMessageSid: messageDto.twilio_message_sid,
    sentAt: messageDto.sent_at ? parseISO(messageDto.sent_at) : null,
    receivedAt: messageDto.received_at
      ? parseISO(messageDto.received_at)
      : null,
    deletedAt: messageDto.deleted_at ? parseISO(messageDto.deleted_at) : null,
    createdBy: messageDto.created_by,
  };
}

export function fromCreateMessageRequestToCreateMessageDto(
  request: CreateMessageRequest
): CreateMessageDto {
  const createMessageRequest = CreateMessageRequestSchema.parse(request);

  return {
    business_id: createMessageRequest.businessId,
    conversation_id: createMessageRequest.conversationId,
    phone_number_id: createMessageRequest.phoneNumberId,
    from_number: createMessageRequest.fromNumber,
    to_number: createMessageRequest.toNumber,
    direction: createMessageRequest.direction,
    status: createMessageRequest.status,
    body: createMessageRequest.body,
    created_by: createMessageRequest.createdBy,
  };
}

export function fromUpdateMessageRequestToUpdateMessageDto(
  request: UpdateMessageRequest
): UpdateMessageDto {
  const updateMessageRequest = UpdateMessageRequestSchema.parse(request);

  return {
    status: updateMessageRequest.status,
    twilio_message_sid: updateMessageRequest.twilioMessageSid,
    sent_at: updateMessageRequest.sentAt
      ? updateMessageRequest.sentAt.toISOString()
      : undefined,
    received_at: updateMessageRequest.receivedAt
      ? updateMessageRequest.receivedAt.toISOString()
      : undefined,
  };
}
